// Path resolution — survey authors define alternate routes through the
// survey based on early responses. Each path has an entryCondition; the
// SDK picks the highest-priority enabled path whose condition matches and
// restricts subsequent page traversal to that path's pages, terminating
// at the path's exitTargetPageKey (or falling through to the survey's
// default thank-you when not set).

import type { SurveyPath } from '../_types/variables.js';
import { evaluateConditionGroup } from './logic.js';

interface ResolveActivePathParams {
  paths: SurveyPath[] | undefined;
  responses: Record<string, unknown>;
  variables: Record<string, unknown>;
}

// Returns the highest-priority enabled path whose entryCondition matches.
// Returns undefined when no paths are configured or none match — the
// survey then uses its default sequential page traversal.
export function resolveActivePath({
  paths,
  responses,
  variables,
}: ResolveActivePathParams): SurveyPath | undefined {
  if (!paths || paths.length === 0) return undefined;
  // Sort by priority descending, treating missing priority as 0.
  const sorted = [...paths]
    .filter((path) => path && path.enabled !== false)
    .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  for (const path of sorted) {
    if (!evaluateConditionGroup(path.entryCondition, { responses, variables })) continue;
    return path;
  }
  return undefined;
}

// Given an active path and the current page key, return the next page key
// inside the path. Returns null when the user has reached the end of the
// path (caller should jump to exitTargetPageKey or default thank-you).
export function nextPageInPath(path: SurveyPath, currentPageKey: string): string | null {
  if (!path.pageKeys || path.pageKeys.length === 0) return null;
  const index = path.pageKeys.indexOf(currentPageKey);
  if (index < 0) return path.pageKeys[0] ?? null; // user is off-path; jump to start
  const next = path.pageKeys[index + 1];
  return next ?? null;
}
